/**
 * LocationMapPicker — Google Maps modal for selecting a property's lat/lng.
 *
 * Requires VITE_GOOGLE_MAPS_API_KEY with Maps JavaScript API + Places API enabled.
 *
 * Usage:
 *   <LocationMapPicker
 *     lat={form.latitude}
 *     lng={form.longitude}
 *     onConfirm={(lat, lng) => { ... }}
 *     onClose={() => setOpen(false)}
 *   />
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LatLng {
  lat: number;
  lng: number;
}

interface Props {
  lat?: number | null;
  lng?: number | null;
  onConfirm: (lat: number, lng: number) => void;
  onClose: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;

/** Default center: Mumbai, India */
const DEFAULT_CENTER: LatLng = { lat: 19.076, lng: 72.8777 };
const DEFAULT_ZOOM = 5;
const PINNED_ZOOM = 16;

// ─── Places autocomplete sub-component ───────────────────────────────────────

interface SearchBarProps {
  onPlace: (lat: number, lng: number) => void;
}

function SearchBar({ onPlace }: SearchBarProps) {
  const placesLib = useMapsLibrary('places');
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    if (!placesLib || !inputRef.current) return;

    autocompleteRef.current = new placesLib.Autocomplete(inputRef.current, {
      fields: ['geometry', 'name', 'formatted_address'],
    });

    const listener = autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current!.getPlace();
      const loc = place.geometry?.location;
      if (loc) onPlace(loc.lat(), loc.lng());
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [placesLib, onPlace]);

  return (
    <input
      ref={inputRef}
      type="text"
      placeholder="Search address or place name…"
      aria-label="Search for a location"
      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
    />
  );
}

// ─── Map interaction sub-component ───────────────────────────────────────────

interface MapControllerProps {
  pin: LatLng | null;
  onPinChange: (pos: LatLng) => void;
}

function MapController({ pin, onPinChange }: MapControllerProps) {
  const map = useMap();

  // Fly to a new pin when it changes (e.g. from Places autocomplete)
  useEffect(() => {
    if (!map || !pin) return;
    map.panTo(pin);
    if ((map.getZoom() ?? 0) < PINNED_ZOOM) map.setZoom(PINNED_ZOOM);
  }, [map, pin]);

  // Click anywhere on the map to set/move the pin
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) onPinChange({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });
    return () => google.maps.event.removeListener(listener);
  }, [map, onPinChange]);

  return null;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function LocationMapPicker({ lat, lng, onConfirm, onClose }: Props) {
  const hasInitial = lat != null && lng != null;
  const [pin, setPin] = useState<LatLng | null>(hasInitial ? { lat: lat!, lng: lng! } : null);
  const initialCenter = hasInitial ? { lat: lat!, lng: lng! } : DEFAULT_CENTER;
  const initialZoom = hasInitial ? PINNED_ZOOM : DEFAULT_ZOOM;

  const handlePlace = useCallback((placeLat: number, placeLng: number) => {
    setPin({ lat: placeLat, lng: placeLng });
  }, []);

  const handlePinChange = useCallback((pos: LatLng) => {
    setPin(pos);
  }, []);

  if (!API_KEY) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl text-center space-y-4">
          <p className="text-2xl">🗺️</p>
          <h2 className="text-base font-semibold text-gray-900">Google Maps not configured</h2>
          <p className="text-sm text-gray-500">
            Set <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">VITE_GOOGLE_MAPS_API_KEY</code> in your <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">.env</code> file to enable the map picker.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-900 px-5 py-2 text-sm font-medium text-white hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    // Backdrop-only click/keydown-to-close; the actual dialog semantics (role, aria-modal,
    // aria-label) live on the inner content div below, not here, since this element has no
    // activation behavior of its own beyond dismissing on an outside click/Escape.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Pick property location on map"
        className="flex w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
      >

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Pin your property location</h2>
            <p className="mt-0.5 text-xs text-gray-500">Search an address or click the map to drop a pin</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close map picker"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Google Maps */}
        <APIProvider apiKey={API_KEY}>
          {/* Search bar — lives inside APIProvider so Places lib is available */}
          <div className="px-5 py-3 border-b border-gray-100">
            <SearchBar onPlace={handlePlace} />
          </div>

          {/* Map */}
          <div className="h-[380px] w-full">
            <Map
              defaultCenter={initialCenter}
              defaultZoom={initialZoom}
              mapId="bookbed-location-picker"
              gestureHandling="greedy"
              disableDefaultUI={false}
              style={{ width: '100%', height: '100%' }}
            >
              <MapController pin={pin} onPinChange={handlePinChange} />
              {pin && (
                <AdvancedMarker
                  position={pin}
                  draggable
                  onDragEnd={(e) => {
                    const pos = e.latLng;
                    if (pos) setPin({ lat: pos.lat(), lng: pos.lng() });
                  }}
                  title="Drag to adjust"
                />
              )}
            </Map>
          </div>
        </APIProvider>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-4">
          <p className="text-sm text-gray-600">
            {pin ? (
              <>
                <span className="font-medium text-gray-900">Pinned: </span>
                <span className="font-mono">{pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}</span>
              </>
            ) : (
              <span className="italic text-gray-400">Click the map to drop a pin</span>
            )}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!pin}
              onClick={() => pin && onConfirm(pin.lat, pin.lng)}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Use this location
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
